import { zipSync } from 'fflate';

import { isMeshPart } from './_utils.ts';
import type { FileExportArray, FileIdx } from '../../../data/data-interfaces.ts';

export interface ZipExportOptions {
  /** fflate compression level 0–9 (default: 6). */
  level?: number;
  /** When true (default), all files are placed at the zip root with no subfolders. Pass false for the legacy per-part folder layout. */
  flat?: boolean;
}

/**
 * Packs a FileExportArray into a zip archive (Uint8Array).
 * Layout:
 *   {partName}/
 *     {partName}.{fileType}        — mesh file
 *     {depName}.{depFileType}      — deps nested under their parent part
 *   {packageDep}.{fileType}        — package-level deps (no parent) at root
 */
export async function buildZipExport(
  files: FileExportArray,
  opts: ZipExportOptions = {}
): Promise<Uint8Array> {
  const entries: Record<string, Uint8Array> = {};
  const level = (opts.level ?? 6) as any;
  const flat = opts.flat !== false;

  function addEntry(path: string, data: ArrayBuffer): void {
    entries[path] = new Uint8Array(data);
  }

  if (flat) {
    for (const file of files) {
      const name = file.fileName || file.fileKey;
      addEntry(`${name}.${file.fileType}`, file.data);
    }
  } else {
    const partFolders = new Map<FileIdx, string>();

    for (const file of files) {
      if (!isMeshPart(file)) continue;
      const name = file.fileName || file.fileKey;
      partFolders.set(file.fileIdx, name);
      addEntry(`${name}/${name}.${file.fileType}`, file.data);
    }

    for (const file of files) {
      if (isMeshPart(file)) continue;
      const name = file.fileName || file.fileKey;
      const parentFolder = file.parent !== undefined ? partFolders.get(file.parent) : null;
      const prefix = parentFolder ? `${parentFolder}/` : '';
      addEntry(`${prefix}${name}.${file.fileType}`, file.data);
    }
  }

  return zipSync(entries, { level });
}
